import { paths } from '@/routes/paths';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useAuthContext } from '@/auth/hooks';
import { OrionAccess, OrionModule, OrionSubmodule } from '@/types';

export function useNavData() {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  // Accounts
  const accountItems = useMemo(() => {
    const items: any[] = [];

    if (
      user?.checkPermission(OrionModule.ACCOUNT, OrionSubmodule.ACCOUNT.COMPANY, OrionAccess.VIEW)
    ) {
      items.push({
        title: t('Organizations'),
        path: paths.management.accounts.organizations.root,
      });
    }
    if (
      user?.checkPermission(OrionModule.ACCOUNT, OrionSubmodule.ACCOUNT.CONTACT, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Contacts'), path: paths.management.accounts.contacts.root });
    }
    if (
      user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.SUBSCRIPTION,
        OrionAccess.VIEW
      )
    ) {
      items.push({
        title: t('Subscriptions'),
        path: paths.management.accounts.subscriptions.root,
      });
    }
    if (
      user?.checkPermission(OrionModule.ACCOUNT, OrionSubmodule.ACCOUNT.ORDER, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Purchase Orders'), path: paths.management.accounts.orders.root });
    }
    if (
      user?.checkPermission(
        OrionModule.ACCOUNT,
        OrionSubmodule.ACCOUNT.BILLING_ORDER,
        OrionAccess.VIEW
      )
    ) {
      items.push({
        title: t('Billing Orders'),
        path: paths.management.accounts.billingOrders.root,
      });
    }
    if (
      user?.checkPermission(OrionModule.ACCOUNT, OrionSubmodule.ACCOUNT.REQUEST, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Requests'), path: paths.management.accounts.request.root });
    }

    return items;
  }, [user, t]);

  // Portfolio
  const portfolioItems = useMemo(() => {
    const items: any[] = [];

    if (
      user?.checkPermission(OrionModule.PARTNER, OrionSubmodule.PARTNER.PARTNER, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Partners'), path: paths.management.portfolio.partners.root });
    }
    if (
      user?.checkPermission(OrionModule.PARTNER, OrionSubmodule.PARTNER.PRODUCT, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Products'), path: paths.management.portfolio.products.root });
    }
    if (user?.checkPermission(OrionModule.PARTNER, OrionSubmodule.PARTNER.SKU, OrionAccess.VIEW)) {
      items.push({ title: t('SKUs'), path: paths.management.portfolio.skus.root });
    }
    if (user?.checkPermission(OrionModule.PARTNER, OrionSubmodule.PARTNER.PLAN, OrionAccess.VIEW)) {
      items.push({ title: t('Plans'), path: paths.management.portfolio.plans.root });
    }
    if (
      user?.checkPermission(OrionModule.PARTNER, OrionSubmodule.PARTNER.PRICEBOOK, OrionAccess.VIEW)
    ) {
      items.push({ title: t('Pricebook'), path: paths.management.portfolio.pricebook.root });
    }

    return items;
  }, [user, t]);

  const manageItems = useMemo(() => {
    const items: any[] = [];

    if (accountItems.length > 0) {
      items.push({
        title: t('Accounts'),
        path: paths.management.accounts.root,
        icon: 'accountCircle',
        children: accountItems,
      });
    }
    if (portfolioItems.length > 0) {
      items.push({
        title: t('Portfolio'),
        path: paths.management.portfolio.root,
        icon: 'inventory',
        children: portfolioItems,
      });
    }
    if (user?.isAdmin) {
      items.push({
        title: t('Directory'),
        path: paths.management.directory.root,
        icon: 'group',
        children: [
          { title: t('Orioners'), path: paths.management.directory.user.root },
          { title: t('Users'), path: paths.management.directory.usermkt.root },
          { title: t('Teams'), path: paths.management.directory.teams.root },
          { title: t('Roles'), path: paths.management.directory.roles.root },
          { title: t('Transactions'), path: paths.management.directory.banks.root },
        ],
      });
    }

    return items;
  }, [accountItems, portfolioItems, user?.isAdmin, t]);

  // OCC Permissions
  const cloudCompassItems = useMemo(() => {
    const items = [];

    if (
      user?.checkPermission(OrionModule.ALTIO, OrionSubmodule.ALTIO.CLOUD_SUMMARY, OrionAccess.VIEW)
    ) {
      items.push({
        title: t('Cloud Summary'),
        path: paths.cloudCompass.summary.root,
        icon: 'graph',
      });
    }
    if (user?.checkPermission(OrionModule.ALTIO, OrionSubmodule.ALTIO.ACCOUNTS, OrionAccess.VIEW)) {
      items.push({
        title: t('Accounts'),
        path: paths.cloudCompass.root,
        icon: 'cloud',
        children: [
          {
            title: t('AWS Payer Accounts'),
            path: paths.cloudCompass.aws.accounts.payerAccounts.root,
          },
          {
            title: t('GCP Billing Accounts'),
            path: paths.cloudCompass.gcp.accounts.masterBillingAccounts.root,
          },
          {
            title: t('Azure Billing Accounts'),
            path: paths.cloudCompass.azure.accounts.billingAccounts.root,
          },
          {
            title: t('OCI Accounts'),
            path: paths.cloudCompass.oci.tenancies.root,
          },
        ],
      });
    }

    if (user?.checkPermission(OrionModule.ALTIO, OrionSubmodule.ALTIO.REPORTS, OrionAccess.VIEW)) {
      const reportChildren = [
        {
          title: t('Cost Report'),
          path: paths.cloudCompass.reports.tabs.root,
        },
        {
          title: t('Billing Report'),
          path: paths.cloudCompass.reports.billing.root,
        },
        {
          title: t('Payer Account Report'),
          path: paths.cloudCompass.reports.payerAccount.root,
        },
      ];
      if (
        user?.checkPermission(
          OrionModule.ALTIO,
          OrionSubmodule.ALTIO.REPORT_APPROVAL,
          OrionAccess.VIEW
        )
      ) {
        reportChildren.push({
          title: t('Report Approval'),
          path: paths.cloudCompass.reports.approval.root,
        });
      }

      items.push({
        title: t('Reports'),
        path: paths.cloudCompass.reports.root,
        icon: 'reports',
        children: reportChildren,
      });
    }

    if (
      user?.checkPermission(OrionModule.ALTIO, OrionSubmodule.ALTIO.RESOURCES, OrionAccess.VIEW)
    ) {
      items.push({
        title: t('Resources'),
        path: paths.cloudCompass.resources.root,
        icon: 'resources',
        children: [
          {
            title: t('Inventory'),
            path: paths.cloudCompass.resources.inventory.root,
          },
          {
            title: t('Virtual Tags'),
            path: paths.cloudCompass.resources.tags.root,
          },
        ],
      });
    }

    return items;
  }, [user, t]);

  const management = useMemo(
    () => [
      {
        subheader: t('Management'),
        items: [...manageItems],
      },
    ],
    [manageItems, t]
  );

  const cloudCompass = useMemo(() => {
    if (cloudCompassItems.length > 0) {
      return [
        {
          subheader: 'Cloud Altio',
          items: [...cloudCompassItems],
        },
      ];
    }
    return [];
  }, [cloudCompassItems]);

  const data = useMemo(
    () => [
      {
        subheader: t('Overview'),
        items: [
          {
            title: t('Dashboard'),
            path: paths.dashboard.root,
            icon: 'dashboard',
          },
        ],
      },
      ...management,
      ...cloudCompass,
    ],
    [management, cloudCompass, t]
  );

  return data;
}
